<template>
  <div v-if="isLoading">loading...</div>
  <div v-else class="w-full flex flex-col gap-y-4 text-sm">
    <!-- schema-sourced components. on a normal scenario, activeForm is undefined -->
    <template v-if="(formStore.activeForm?.form ?? []).length > 0">
      <CollapsibleSection
        :key="key"
        :header-label="$t(section.header)"
        :body-class="useLayoutClasses(section?.form_style?.web?.layout)?.['bodyClass']"
        v-for="(section, key) in formStore.activeForm?.form"
      >
        <template :key="key" v-for="(field, key) in section.schema">
          <InfoBar v-if="field.component == 'InfoBar'" :text="field.text" />
        </template>
      </CollapsibleSection>
    </template>
    <!-- build schema -->
    <GroupDraftsView></GroupDraftsView>
    <CollapsibleSection
      :key="key"
      :header-label="$t(section.header)"
      body-class="w-full flex flex-wrap"
      v-for="(section, key) in parsedForm.filter((pf) => pf.shouldDisplay)"
    >
      <div
        :key="key"
        :class="[field.component == 'AddressFormGroup' ? 'w-full' : 'w-1/2']"
        :data-for="field.field_name"
        class="p-1.5 flex flex-col"
        v-for="(field, key) in section.schema.filter((s) => s.showField)"
      >
        <!-- AddressFormGroup specific UI -->
        <div v-if="field.component == 'AddressFormGroup'" class="w-full flex flex-wrap">
          <div
            :key="key"
            class="p-1.5 w-1/2 flex flex-col"
            v-for="(addressField, key) in field.value.filter((s) => s.showField)"
          >
            <span
              :key="`${key}-${addressField.field_name}`"
              :data-field-name="addressField.field_name"
              class="font-medium"
            >
              {{ $t(addressField.label || '--') }}
            </span>

            <span class="text-brand-black-autofill ltr:font-sans text-xs leading-normal break-all">
              {{ addressField.value || '--' }}
            </span>
          </div>
        </div>
        <!-- normal field-value UI -->
        <template v-else>
          <span
            :key="`${key}-${field.field_name}`"
            :data-field-name="field.field_name"
            class="font-medium"
          >
            {{ $t(field.review_label || field.label || '--') }}
          </span>
          <p class="text-brand-black-autofill ltr:font-sans text-xs leading-normal break-all">
            <span v-if="field.appendages?.bottom">{{ field.appendages?.bottom }}</span>
            <span>
              {{ $t(field.value || '--') }}
            </span>
          </p>
        </template>
      </div>
    </CollapsibleSection>
    <CollapsibleSection
      v-if="hasAttachments"
      :header-label="$t('attachments')"
      class="w-full"
      body-class="flex flex-col md:flex-wrap gap-y-2"
    >
      <div
        :key="key"
        v-for="(attachment, key) in formStore.draft.attachments || formStore.attachments"
        class="w-full md:w-1/2 flex flex-row gap-x-2"
      >
        <span class="font-medium">{{ attachment.attachmentType.localizedName }}</span>
        <button
          class="ltr:font-sans leading-normal text-xs text-primary"
          @click="onDownloadAttachment(attachment)"
        >
          {{ $t('View') }}
        </button>
      </div>
    </CollapsibleSection>

    <div class="flex justify-between">
      <FilledButton class="px-5 py-1.5 text-white" @click="$emit('onBack')">
        {{ $t('back') }}
      </FilledButton>

      <FilledButton class="px-5 py-1.5 text-white" @click="onSubmit">
        {{ $t('next') }}
      </FilledButton>
    </div>
  </div>
</template>

<script setup>
import FilledButton from '@/components/Buttons/Filled.vue'
import GroupDraftsView from '@/components/Form/GroupDrafts/view.vue'
import InfoBar from '@/components/Form/InfoBar.vue'
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import useDropdownActions from '@/composables/actions/useDropdownActions'
import usePreReviewSubmit from '@/composables/preReviewSubmit/index'
import useSectionsToHideOverride from '@/composables/sections_to_hide_override/index'
import useDropdownOptions from '@/composables/useDropdownOptions'
import useFile from '@/composables/useFile.js'
import useLayoutClasses from '@/composables/useLayoutClasses'
import useVisible from '@/composables/useVisible'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import lodashGet from 'lodash/get'
import { DateTime } from 'luxon'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const globalStore = useGlobalStore()
const formStore = useFormStore()
const emits = defineEmits('onNext')
const { parseOptions, fetchOptions } = useDropdownOptions()
const route = useRoute()
const { findShowRule, getBindObj } = useVisible()
const i18n = useI18n()

const parsedForm = ref()
const isLoading = ref(true)

const componentsToHide = ['InfoBar', 'Spacer', 'DownloadLink']

/**
 * basically remove all recaptcha and notes section from the list
 */
const sectionsForDisplayFunc = async () => {
  let sectionsToHide = [
    'verifyApplicant',
    'notes',
    'importantNote',
    '',
    ...(await useSectionsToHideOverride(route.params.serviceId))
  ]
  const forms = formStore.schema.steps.map((s) => s.form)
  return forms.map((form) => form.filter((f) => sectionsToHide.includes(f.header) === false)).flat()
}

const getValue = (field) => {
  return (
    formStore.draft.request[field.field_name] ||
    formStore.form.values[field.field_name] ||
    lodashGet(formStore.draft.request, field.field_name) ||
    lodashGet(formStore.draft.request, field?.preview_field_override?.name)
  )
}

const formatAccordionList = async () => {
  let sectionsForDisplay = await sectionsForDisplayFunc()
  const promises = sectionsForDisplay
    .filter((field) => field.device_type != 'mobile')
    .map(async ({ header, schema, showRules }) => {
      // section level showRules
      const shouldDisplay = showRules
        ? findShowRule(
            showRules.all || showRules.any,
            showRules.all ? 'all' : 'any',
            getBindObj(route.params.serviceId)
          )
        : true

      const tempSchema = await Promise.all(
        schema
          .filter((field) => field.device_type != 'mobile')
          .filter((field) => componentsToHide.includes(field.component) === false)
          /**
           * filter out ignored_fields due to the fact that it won't be
           * included in the form.values and draft.request
           */
          .filter((field) => {
            const isIgnored = formStore.schema.api.ignore_fields?.includes(field.field_name)
            return !isIgnored
          })
          .map(processField)
      )

      return {
        header,
        shouldDisplay,
        schema: tempSchema
      }
    })

  parsedForm.value = await Promise.all(promises)
}

onMounted(async () => {
  await formatAccordionList()
  isLoading.value = false
})

// const isIdentifierIgnored = computed(() => {
//   return formStore.schema.api.ignore_fields?.includes('selectIdentificater')
// })

/**
 * controls the attachments section of the review page
 */
const hasAttachments = computed(() => {
  // check if there's an "attachments" step in the schema
  const hasAttachmentsStep = formStore.schema.stepLabels.web.includes('attachments')

  // check if there's attachments left for upload
  const hasAttachmentsToUpload = formStore.hasAttachmentsToUpload

  return hasAttachmentsStep && hasAttachmentsToUpload
})

//#region	process field
const processField = async (field) => {
  const isNeedShow = field.showRules
    ? findShowRule(
        field.showRules.all || field.showRules.any,
        field.showRules.all ? 'all' : 'any',
        {
          ...getBindObj(route.params.serviceId),
          ...formStore.draft.request
        }
      )
    : true

  let value = getValue(field)

  if (field.component == 'AutocompleteDropdown') {
    value = formStore.draft.request.profession?.text
  }

  if (field.component == 'DropdownSearch' && isNeedShow) {
    /**
     * for fields like draft.request.portId, etc
     * find the corresponding value by fetching the dropdown api
     * and either calling it again or finding it in the cache.
     * finally returns the label.
     */
    value = await processDropdownSearch(field)
  }

  if (field.component == 'MultipleDropdownSearch' && isNeedShow) {
    value = await processMultipleDropdownSearch(field)
  }

  if (field.component == 'AddressFormGroup') {
    value = await processAddressFormGroup(field)
  }

  if (field.component == 'RadioGroup') {
    value = await processRadioGroup(field)
  }

  if (field.component == 'DatepickerCalendar') {
    value = await processDate(field)
  }

  return {
    ...field,
    // component level showRules
    showField: isNeedShow,
    value
  }
}

//#region	process dropdown
const processDropdownSearch = async (field, option = null) => {
  const { field_name, options } = field

  const optionValue =
    option || formStore.draft.request[field_name] || formStore.form.values[field_name]

  /**
   * handles displaying the label when options is already provided in the schema
   * example: numberOfYears provides the options [1,2]
   * see form_727.json
   */
  if ((options.value || []).length > 0) {
    const matchingOption =
      options?.value?.find(
        (o) => o.value === optionValue || o.id === optionValue || o.code === optionValue
      ) || []

    // probably need to rely on the structure, but can also achieve using this
    const possibleLabelKeys = ['label', 'localizedName', 'text']
    let matchingLabel
    possibleLabelKeys.forEach((key) => {
      if (key in matchingOption) {
        matchingLabel = matchingOption[key]
      }
    })

    return matchingLabel
  }

  if (field.field_name == 'transactionReasonId') {
    const { request } = formStore.draft
    return request?.transactionReason?.text ?? '--'
  }

  if (options?.api) {
    const {
      api: { path, query }
    } = options

    const storagePath = `${path}?${new URLSearchParams(query).toString()}`
    let cachedOptions = JSON.parse(sessionStorage.getItem(storagePath) || '[]')
    if ((cachedOptions || []).length === 0) {
      cachedOptions = await fetchOptions(
        {
          ...options,
          api: {
            ...options.api,
            needProcess: true,
            need_cache: false
          }
        },
        {
          dependentOn: field.dependent_on
        }
      )
    }

    if (options.api.on_success?.action) {
      const { action, action_params } = options.api.on_success
      cachedOptions = await useDropdownActions()[action](cachedOptions, action_params)
    }

    cachedOptions = await parseOptions(cachedOptions, options)

    if (optionValue) {
      //There don't have the key of text in options of numberOfYears, which has label
      let neededObj =
        cachedOptions?.find((o) => {
          return o.value === optionValue || o.id === optionValue || o.code === optionValue
        }) || {}
      return neededObj?.text || neededObj?.label
    }

    return '--'
  }
}
//#endregion

//#region	process form group
const processAddressFormGroup = async (field) => {
  const { template_schema } = field
  return await Promise.all(
    template_schema
      .filter((field) => field.device_type != 'mobile')
      .filter((field) => componentsToHide.includes(field.component) === false)
      .map(processField)
  )
}
//#endregion

//#region process multiple dropdown search
const processMultipleDropdownSearch = async (field) => {
  const { field_name } = field

  let optionValue = formStore.draft.request[field_name] || formStore.form.values[field_name]

  // optionValue should always be an array
  if (!Array.isArray(optionValue) && optionValue.includes(',')) {
    optionValue = optionValue.split(',')
  }

  const results = await Promise.all(
    optionValue.map(async (value) => {
      return await processDropdownSearch(field, value)
    })
  )

  return results.join(', ')
}
//#endregion

//#region process radio group
const processRadioGroup = async (field) => {
  const { radio_options } = field
  const value = getValue(field)

  const option = radio_options.find((ro) => ro.value == value)

  if (!option) return '--'
  return i18n.t(option.label)
}
//#endregion

const processDate = async (field) => {
  const { server_date_format } = field
  const value = getValue(field)

  return DateTime.fromISO(value).toFormat(server_date_format)
}

const onDownloadAttachment = async (attachment) => {
  const {
    id,
    token,
    attachmentType: { localizedName }
  } = attachment
  await useFile().getAttachment(...[id, token, localizedName])
}

const onSubmit = async () => {
  const payload = {
    // Due to vee-validate not allowing direct value setting, it requires copying and subsequent processing.
    request: { ...formStore.form.values },
    serviceTransactionId: globalStore.startedService.serviceTransactionId,
    administrativeRegionId: 1
  }
  if (!(await usePreReviewSubmit(payload.serviceTransactionId, payload))) return
  emits('onNext')
}
</script>

<style lang="scss" scoped></style>
